// SPDX-License-Identifier: Apache-2.0

// Shared helpers for the heavy ingest parsers.
//
// Every public parser in this package agrees to:
//   - Run entirely in the user's browser (local-first posture).
//   - Never throw on malformed input — instead, return an IngestResult with
//     `kind:'txt'` and a `[corrupt or unsupported file: <name>]` body, plus a
//     console.warn breadcrumb that surfaces in DevTools.
//   - Warn (not throw) on >10 MB inputs so the SPA can still attempt the parse.
//
// `IngestResult` mirrors the shape in @tether/storage's schema. We declare it
// locally rather than import from storage to keep the dependency graph one-way
// (storage imports ingest, never the reverse). Storage's schema.ts owns the
// canonical version; if it ever drifts, this declaration is the second source
// of truth — keep the two in sync by hand.

/** Mirror of @tether/storage's `VaultFrontmatter` — non-md ingests always
 *  yield `{}` for this, so the loose shape is enough. */
export type IngestFrontmatter = Record<string, unknown>;

export type IngestKind = 'md' | 'txt' | 'docx' | 'pdf' | 'xlsx' | 'csv' | 'udf';

export interface IngestResult {
  title: string;
  /** Markdown body (sans frontmatter). */
  content: string;
  /** Parsed frontmatter (md only — other kinds yield `{}`). */
  front: IngestFrontmatter;
  kind: IngestKind;
  /** Optional FSAccess path relative to the granted vault directory. */
  path?: string;
}

const TEN_MB = 10 * 1024 * 1024;

/** Files larger than 10 MB get a console.warn but the parser still tries. */
export function warnIfLarge(file: { name: string; size: number }, kind: string): void {
  if (file.size > TEN_MB) {
    console.warn(
      `[ingest:${kind}] '${file.name}' is ${(file.size / (1024 * 1024)).toFixed(1)} MB — parse may be slow`,
    );
  }
}

/** Build the canonical title for an ingested non-md file: stem + ".md". */
export function baseTitle(name: string): string {
  const i = name.lastIndexOf('.');
  const stem = i < 0 ? name : name.slice(0, i);
  return `${stem.trim() || 'imported'}.md`;
}

/** Wrap a body with an H1 of the title (sans the `.md` suffix). Mirrors the
 *  shape `packages/storage/ingest.ts` already uses for txt. */
export function wrapWithTitle(title: string, body: string): string {
  const header = `# ${title.replace(/\.md$/i, '')}`;
  const trimmed = body.trimEnd();
  return trimmed ? `${header}\n\n${trimmed}\n` : `${header}\n`;
}

/** Build an IngestResult representing a failed parse. The dispatcher in
 *  @tether/storage relies on parsers never throwing — bad files come back as
 *  txt with a clear corruption marker so the SPA can still surface them. */
export function corruptResult(name: string, reason: string): IngestResult {
  console.warn(`[ingest] '${name}' failed to parse:`, reason);
  return {
    title: baseTitle(name),
    content: `[corrupt or unsupported file: ${name}]`,
    front: {},
    kind: 'txt',
  };
}

/** Collapse blank-line runs and trim trailing whitespace per line. Same shape
 *  as `asParagraphs` in @tether/storage's ingest.ts — kept private to each
 *  package so neither has to import the other for one helper. */
export function asParagraphs(raw: string): string {
  return raw
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((l) => l.replace(/[\t ]+$/g, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
